import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Stepper,
    Step,
    StepLabel,
    Box,
    Typography,
    Alert,
    Autocomplete,
    Paper,
    CircularProgress,
    LinearProgress,
    Card,
    CardMedia,
    Chip,
    IconButton
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { addMonths, isBefore, isAfter, format, addDays, addYears } from 'date-fns';
import PropTypes from 'prop-types';
import { 
    createCode, 
    updateCode, 
    checkCodeAvailability as checkCodeAvailabilityApi,
    validateUrl,
    getPaidCodes,
    checkContentModeration,
    uploadImage,
    getCodeOwnerDetails,
    getParameters,
    createTempCode,
    deleteTempCode,
    convertTempCode,
    getCodeSuggestions
} from '../../services/codeManagementApi';
import { styled } from '@mui/material/styles';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PendingIcon from '@mui/icons-material/Pending';
import { normalizeUrl } from '../../services/urlUtils';
import CloseIcon from '@mui/icons-material/Close';
import { debounce } from 'lodash';
import ImageUploadDialog from './ImageUploadDialog';
import { ReactCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { optimizeImage, createImagePreview, calculateInitialCrop, getCroppedImage } from './ImageHelper';
import moment from 'moment-timezone';
import PaymentForm from './PaymentForm';

const getSteps = (isEditMode) => [
    'Guide',
    'Enter URL',
    'Select Period',
    isEditMode ? 'View Code' : 'Choose Code',
    'Add Details',
    'Add Image'
];

const MAX_RENTAL_MONTHS = 3;

// Update code format constant
const CODE_REGEX = /^[A-Z]{2,3}[0-9]{4}$/;
const MAX_PREFIX_LENGTH = 3;

// Add input validation helper
const isValidCodeInput = (input, position) => {
    const char = input.toUpperCase();
    if (position <= 2) {
        return /^[A-Z]$/.test(char);
    }
    if (position === 3) {
        return /^[A-Z0-9]$/.test(char);
    }
    return /^[0-9]$/.test(char);
};

// Styled components
const StyledTextField = styled(TextField)(({ theme }) => {
    const isDark = theme.palette.mode === 'dark';
    return {
        '& .MuiOutlinedInput-root': {
            backgroundColor: isDark ? '#141E30' : '#FFFFFF',
            borderRadius: '12px',
            '& fieldset': {
                borderColor: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(15, 23, 42, 0.12)',
                borderWidth: '1px'
            },
            '&:hover fieldset': {
                borderColor: theme.palette.primary.main,
            },
            '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
                borderWidth: '2px'
            },
            '&.Mui-error fieldset': {
                borderColor: theme.palette.error.main,
            }
        },
        '& .MuiInputLabel-root': {
            color: theme.palette.text.secondary
        }
    };
});

const StyledStepper = styled(Stepper)(({ theme }) => {
    const isDark = theme.palette.mode === 'dark';
    return {
        marginBottom: theme.spacing(2),
        '& .MuiStepLabel-root': {
            '& .MuiStepLabel-label': {
                color: theme.palette.text.secondary,
                fontWeight: 500,
                '&.Mui-active': {
                    color: theme.palette.text.primary,
                    fontWeight: 700
                },
                '&.Mui-completed': {
                    color: theme.palette.text.secondary,
                    fontWeight: 600
                }
            }
        },
        '& .MuiStepIcon-root': {
            color: isDark ? 'rgba(255, 255, 255, 0.18)' : 'rgba(15, 23, 42, 0.18)',
            '&.Mui-active': {
                color: theme.palette.primary.main
            },
            '&.Mui-completed': {
                color: theme.palette.success.main
            }
        },
        '& .MuiStepConnector-line': {
            borderColor: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(15, 23, 42, 0.12)'
        }
    };
});

const StyledDateField = styled(TextField)(({ theme }) => {
    const isDark = theme.palette.mode === 'dark';
    return {
        '& .MuiOutlinedInput-root': {
            backgroundColor: isDark ? '#141E30' : '#FFFFFF',
            borderRadius: '12px',
            '& fieldset': {
                borderColor: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(15, 23, 42, 0.12)',
                borderWidth: '1px'
            },
            '&:hover fieldset': {
                borderColor: theme.palette.primary.main,
            },
            '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
                borderWidth: '2px'
            }
        },
        '& .MuiInputLabel-root': {
            color: theme.palette.text.secondary
        },
        '& .MuiInputBase-input': {
            fontSize: '1rem',
            padding: '12px 14px'
        }
    };
});

// Update validation line component
const ValidationLine = ({ label, status, content }) => (
    <Box sx={{ 
        display: 'flex', 
        alignItems: 'center',
        minHeight: { xs: '44px', sm: '52px' },
        gap: { xs: 1, sm: 2.5 },
        py: { xs: 0.75, sm: 1.25 },
        borderBottom: '1px solid',
        borderColor: 'divider'
    }}>
        <Box sx={{ 
            width: { xs: '95px', sm: '140px' },
            minWidth: { xs: '95px', sm: '140px' },
            display: 'flex',
            alignItems: 'center'
        }}>
            <Typography 
                component="span" 
                color="text.secondary"
                sx={{
                    fontSize: {
                        xs: '0.85rem',
                        sm: '0.95rem'
                    },
                    fontWeight: 600
                }}
            >
                {label}
            </Typography>
        </Box>
        <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            gap: { xs: 1, sm: 2 },
            flex: 1
        }}>
            <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                minWidth: { xs: '22px', sm: '26px' }
            }}>
                {status === 'pending' && (
                    <CircularProgress 
                        size={22}
                        thickness={5}
                        sx={{ color: 'primary.main' }}
                    />
                )}
                {status === 'success' && <CheckCircleIcon sx={{ fontSize: { xs: 22, sm: 26 }, color: '#10B981' }} />}
                {status === 'error' && <ErrorIcon sx={{ fontSize: { xs: 22, sm: 26 }, color: '#EF4444' }} />}
                {status === 'inactive' && (
                    <Box sx={{ 
                        width: { xs: 18, sm: 22 }, 
                        height: { xs: 18, sm: 22 }, 
                        borderRadius: '50%',
                        border: '2px dashed',
                        borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(15, 23, 42, 0.2)'
                    }} />
                )}
            </Box>
            <Typography 
                component="span"
                sx={{
                    fontSize: {
                        xs: '0.9rem',
                        sm: '0.95rem'
                    },
                    fontWeight: 500,
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    color: status === 'success' ? '#10B981' : 
                           status === 'error' ? '#EF4444' : 
                           'text.secondary'
                }}
            >
                {content}
            </Typography>
        </Box>
    </Box>
);

// Add price display component
const CodePriceSection = ({ code, price }) => (
    <Box sx={{ 
        display: 'flex', 
        alignItems: 'center',
        height: '56px', // Match TextField height
        width: 'fit-content', // Minimum width to fit content
    }}>
        <Typography 
            variant="body1" 
            sx={{ 
                color: theme => price > 0 ? 
                    theme.palette.primary.main : 
                    theme.palette.success.main,
                fontWeight: 'medium'
            }}
        >
            ${price}
        </Typography>
    </Box>
);

// First, let's extract the validation message logic into a separate function
const getValidationMessage = (code) => {
    if (!code) return 'Enter code...';
    const letterPrefix = code.replace(/[0-9]/g, '');
    const numberPart = code.replace(/[A-Z]/g, '');
    
    // Validate prefix first
    if (letterPrefix.length > 3) {
        return 'Prefix too long. Use 2 or 3 letters only';
    }
    if (letterPrefix.length === 1) {
        return 'Start with 2 or 3 letters (Country or City code)';
    }
    if (letterPrefix.length === 2) {
        // For 2 letters, suggest the optional third letter
        if (numberPart.length === 0) {
            return 'Continue... 1 letter (optional) and 4 numbers';
        }
        if (numberPart.length < 4) {
            const remaining = 4 - numberPart.length;
            return `Continue... need ${remaining} more ${remaining === 1 ? 'number' : 'numbers'}`;
        }
        if (numberPart.length > 4) {
            return 'Too many numbers. Use exactly 4 numbers';
        }
    }
    if (letterPrefix.length === 3) {
        // For 3 letters, only check numbers
        if (numberPart.length < 4) {
            const remaining = 4 - numberPart.length;
            return `Continue... need ${remaining} more ${remaining === 1 ? 'number' : 'numbers'}`;
        }
        if (numberPart.length > 4) {
            return 'Too many numbers. Use exactly 4 numbers';
        }
    }
    
    // If we have correct length but invalid format
    if (!CODE_REGEX.test(code)) {
        return 'Invalid format. Must be 2 or 3 letters followed by 4 numbers';
    }
    
    return 'Valid code format';
};

// Add this helper function at the top level
const formatCodeInput = (value) => {
    const letters = value.replace(/[^A-Z]/g, '').slice(0, 3);
    const numbers = value.replace(/[^0-9]/g, '').slice(0, 4);
    return `${letters}${numbers}`;
};

// Update the CodeSuggestions component
const CodeSuggestions = ({ validationMessage, suggestions, onSelect, freeCodesRemaining, incrementalPrice }) => {
    // Check if code is complete
    const isComplete = validationMessage === 'Valid code format';
    
    // Don't show suggestions if code is complete
    if (isComplete) {
        return null;
    }

    // Check if we have any suggestions to show
    const hasSuggestions = suggestions.paid.length > 0 || suggestions.free.length > 0;

    return (
        <Paper sx={{
            mt: 0.5,
            maxHeight: 300,
            bgcolor: 'background.paper',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column'
        }}>
            {/* Validation message (always shown) */}
            <Box sx={{
                p: 1.5,
                borderBottom: hasSuggestions ? '1px solid' : 'none',
                borderColor: 'divider',
                bgcolor: 'background.paper'
            }}>
                <Typography variant="body2" color="text.secondary">
                    {validationMessage}
                </Typography>
            </Box>

            {/* Show suggestions if we have any */}
            {hasSuggestions && (
                <>
                    <Box sx={{
                        position: 'sticky',
                        top: 0,
                        bgcolor: 'background.paper',
                        zIndex: 1,
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Typography variant="caption" sx={{ px: 1.5, py: 0.5, display: 'block', color: 'text.secondary' }}>
                            Some examples you can use:
                        </Typography>
                    </Box>

                    <Box sx={{ overflow: 'auto' }}>
                        {suggestions.paid.map((suggestion, index) => (
                            <Box
                                key={`paid-${index}`}
                                sx={{
                                    p: 1.0,
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: 'action.hover' },
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}
                                onClick={() => onSelect(suggestion.value)}
                            >
                                <Typography>{suggestion.value}</Typography>
                                <Typography 
                                    variant="caption" 
                                    sx={{ color: theme => theme.palette.primary.main }}
                                >
                                    ${suggestion.price}
                                </Typography>
                            </Box>
                        ))}

                        {suggestions.free.map((suggestion, index) => (
                            <Box
                                key={`free-${index}`}
                                sx={{
                                    p: 1.0,
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: 'action.hover' },
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}
                                onClick={() => onSelect(suggestion.value)}
                            >
                                <Typography>{suggestion.value}</Typography>
                                <Typography 
                                    variant="caption" 
                                    sx={{ color: theme => freeCodesRemaining > 0 ? theme.palette.success.main : theme.palette.primary.main }}
                                >
                                    ${freeCodesRemaining > 0 ? '0' : (incrementalPrice / 100).toFixed(2)}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </>
            )}
        </Paper>
    );
};

// Update CodeAvailabilityCheck component
const CodeAvailabilityCheck = ({ 
    code, 
    validFrom, 
    validUntil, 
    overlappingPeriods = [], 
    isChecking = false, 
    paidCode = null 
}) => {
    if (isChecking) {
        return (
            <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, textAlign: 'center' }}>
                <CircularProgress size={24} sx={{ mb: 2 }} />
                <Typography color="text.secondary">
                    Checking availability...
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
            {overlappingPeriods.length > 0 ? (
                <>
                    <Typography color="error.main" variant="subtitle1" sx={{ mb: 2 }}>
                        Code is not available for selected period
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Would you like to select a different code or different period?
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                        {overlappingPeriods.map((period, index) => (
                            <Box key={index} sx={{ 
                                p: 1, 
                                bgcolor: 'error.light',
                                borderRadius: 1,
                                mb: 1
                            }}>
                                <Typography variant="caption">
                                    {format(new Date(period.valid_from), 'dd MMM yyyy')} - 
                                    {format(new Date(period.valid_until), 'dd MMM yyyy')}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </>
            ) : (
                <Box sx={{ textAlign: 'center' }}>
                    <CheckCircleIcon color="success" sx={{ fontSize: 48, mb: 2 }} />
                    <Typography color="success.main" variant="subtitle1">
                        Code is available for selected period
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

// Add prop types validation
CodeAvailabilityCheck.propTypes = {
    code: PropTypes.string.isRequired,
    validFrom: PropTypes.instanceOf(Date),
    validUntil: PropTypes.instanceOf(Date),
    overlappingPeriods: PropTypes.arrayOf(PropTypes.shape({
        valid_from: PropTypes.string,
        valid_until: PropTypes.string
    })),
    isChecking: PropTypes.bool,
    paidCode: PropTypes.oneOfType([  // Allow both string and object
        PropTypes.string,
        PropTypes.object
    ])
};

// Add styled components
const DropZone = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isDragging'
})(({ theme, isDragging }) => ({
    width: '100%',
    height: '400px',
    border: `2px dashed ${isDragging ? theme.palette.primary.main : theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
    cursor: 'pointer',
    transition: 'border-color 0.2s ease',
    backgroundColor: isDragging ? theme.palette.action.hover : 'transparent',
    '&:hover': {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.action.hover
    }
}));

// Update the AvailabilityStatus component
const AvailabilityStatus = ({ isAvailable, overlappingPeriods }) => {
    if (isAvailable) {
        return (
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: 2,
                p: 1
            }}>
                <CheckCircleIcon color="success" />
                <Typography sx={{ color: theme => theme.palette.success.main }}>
                    Code is available for selected period
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography sx={{ p: 1, color: theme => theme.palette.error.main }}>
                Code is already in use during:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {overlappingPeriods.map((period, index) => (
                    <Box 
                        key={index} 
                        sx={{ 
                            p: 1,
                            '&:not(:last-child)': {
                                borderBottom: '1px solid',
                                borderColor: 'divider'
                            }
                        }}
                    >
                        <Typography variant="body2" sx={{ 
                            display: 'flex',
                            gap: 1,
                            color: theme => theme.palette.text.primary
                        }}>
                            <span>
                                {format(new Date(period.valid_from), 'MMM dd, yyyy')} - {format(new Date(period.valid_until), 'MMM dd, yyyy')}
                            </span>
                            <Box 
                                component="span" 
                                sx={{ 
                                    display: { xs: 'none', sm: 'inline' },
                                    color: theme => theme.palette.text.secondary 
                                }}
                            >
                                /
                            </Box>
                            <span sx={{ color: theme => theme.palette.text.secondary }}>
                                {period.code_url}
                            </span>
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

const CodeForm = ({ open, onClose, editingCode, onCodeSaved }) => {
    const [activeStep, setActiveStep] = useState(0);
    const [codeForm, setCodeForm] = useState({
        code_url: '',
        valid_from: new Date(),
        valid_until: addMonths(new Date(), 1),
        code_value: '',
        code_name: '',
        code_description: '',
        image_url: ''
    });
    const [formErrors, setFormErrors] = useState({});
    const [availableCodes, setAvailableCodes] = useState([]);
    const [overlappingCodes, setOverlappingCodes] = useState([]);
    const [codeInput, setCodeInput] = useState('');
    const [suggestedCodes, setSuggestedCodes] = useState([]);
    const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
    const [overlappingCode, setOverlappingCode] = useState(null);
    const [validationStatus, setValidationStatus] = useState({
        format: { status: 'inactive' },
        preview: { status: 'inactive' },
        existing: { status: 'inactive' }
    });
    const [selectedCodes, setSelectedCodes] = useState([]);
    const [codeSuggestions, setCodeSuggestions] = useState([]);
    const [overlappingPeriods, setOverlappingPeriods] = useState([]);
    const [checkingAvailability, setCheckingAvailability] = useState(false);
    const [codeAvailability, setCodeAvailability] = useState({
        available: false,
        paidCode: null,
        overlappingPeriods: []
    });
    const [allPaidCodes, setAllPaidCodes] = useState([]);
    const [selectedCode, setSelectedCode] = useState(null);
    const [imageUploadOpen, setImageUploadOpen] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [crop, setCrop] = useState(undefined);
    const [completedCrop, setCompletedCrop] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [processedImage, setProcessedImage] = useState(null);
    const [isImageUploaded, setIsImageUploaded] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [filteredSuggestions, setFilteredSuggestions] = useState({
        paid: [],
        free: []
    });
    const [paidCodes, setPaidCodes] = useState([]);
    const [freeCodesRemaining, setFreeCodesRemaining] = useState(null);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [paymentDetails, setPaymentDetails] = useState(null);
    const [incrementalPrice, setIncrementalPrice] = useState(0);
    const [isPriceChecking, setIsPriceChecking] = useState(false);
    const [paymentProcessing, setPaymentProcessing] = useState(false);
    const [requiresPayment, setRequiresPayment] = useState(false);
    const [fromPickerOpen, setFromPickerOpen] = useState(false);
    const [untilPickerOpen, setUntilPickerOpen] = useState(false);
    const [isAvailabilityCheck, setIsAvailabilityCheck] = useState(false);
    const [tempCodeId, setTempCodeId] = useState(null);
    const [paymentTimer, setPaymentTimer] = useState(null);
    const paymentFormRef = useRef(null);
    const [error, setError] = useState(null);
    const [paymentTimeLeft, setPaymentTimeLeft] = useState(120); // 2 minutes in seconds
    const [paymentTimerRef, setPaymentTimerRef] = useState(null);

    // Add getCodeData function to prepare code data
    const getCodeData = useCallback(() => {
        return {
            code_value: codeForm.code_value,
            code_url: codeForm.code_url,
            valid_from: codeForm.valid_from,
            valid_until: codeForm.valid_until,
            code_name: codeForm.code_name,
            code_description: codeForm.code_description,
            image_url: codeForm.image_url
        };
    }, [codeForm]);

    const handlePaymentCancel = async (tempCodeId) => {
        if (tempCodeId) {
            try {
                await deleteTempCode(tempCodeId);
                console.log('Temporary code cleaned up:', tempCodeId);
            } catch (error) {
                console.error('Failed to cleanup temporary code:', error);
            }
        }
    };

    // Add this effect to manage the payment timer
    useEffect(() => {
        if (showPaymentModal) {
            // Start timer when payment modal shows
            const timer = setInterval(() => {
                setPaymentTimeLeft(prevTime => {
                    if (prevTime <= 1) {
                        clearInterval(timer);
                        handlePaymentModalClose(null, 'timeExpired');
                        return 0;
                    }
                    return prevTime - 1;
                });
            }, 1000);
            
            setPaymentTimerRef(timer);
            // Reset timer to full time (10 minutes)
            setPaymentTimeLeft(120);
            
            return () => {
                clearInterval(timer);
                setPaymentTimerRef(null);
            };
        }
    }, [showPaymentModal]);

    // Update the handlePaymentModalClose function to handle timeout
    const handlePaymentModalClose = useCallback((event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown' || 
            reason === 'buttonClick' || reason === 'timeExpired') {
            
            // Clear the timer
            if (paymentTimerRef) {
                clearInterval(paymentTimerRef);
                setPaymentTimerRef(null);
            }

            // Cleanup payment form
            if (paymentFormRef.current) {
                paymentFormRef.current.cleanup();
            }

            setShowPaymentModal(false);
        }
    }, [paymentTimerRef]);

    // Update handleFinalSubmit to include final availability check
    const handleFinalSubmit = async () => {
        if (!validateCurrentStep()) return;

        try {
            // Final availability check before proceeding
            const availabilityResult = await checkCodeAvailabilityApi({
                code: codeForm.code_value,
                valid_from: codeForm.valid_from,
                valid_until: codeForm.valid_until,
                exclude_code_id: editingCode?.code_id
            });

            if (!availabilityResult.isAvailable) {
                setError('This code is no longer available for the selected period. Please choose a different code or change dates.');
                // Update availability state to show conflicting periods
                setCodeAvailability(availabilityResult);
                // Go back to the code selection step
                setActiveStep(3);
                return;
            }

            // If code is still available, proceed with payment or creation
            if (requiresPayment && !editingCode) {
                setShowPaymentModal(true);
                return;
            }

            await handleSubmit(true);
        } catch (error) {
            console.error('Error checking code availability:', error);
            setError('Failed to verify code availability. Please try again.');
        }
    };

    // Move cleanup function definition to the top
    const cleanupTempCode = useCallback(async () => {
        if (tempCodeId) {
            try {
                await deleteTempCode(tempCodeId);
                console.log('Temporary code cleaned up:', tempCodeId);
            } catch (error) {
                console.error('Failed to cleanup temporary code:', error);
            }
            setTempCodeId(null);
        }
    }, [tempCodeId]);

    const handlePaymentTimeout = async () => {
        if (paymentFormRef.current) {
            paymentFormRef.current.setUnmountReason('timeout');
        }
        await cleanupTempCode();
        setShowPaymentModal(false);
        onClose();
    };

    // Handle dialog close
    const handleDialogClose = useCallback(async (event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            await cleanupTempCode();
        }
        setShowPaymentModal(false);
        onClose();
    }, [onClose, cleanupTempCode]);

    // Get user's timezone
    const userTimezone = moment.tz.guess();

    // Single fetch for paid codes when form opens
    useEffect(() => {
        if (open) {
            const fetchPaidCodes = async () => {
                try {
                    const response = await getPaidCodes();
                    setAllPaidCodes(response || []);
                    setCodeSuggestions(response || []); // Set both states at once
                } catch (error) {
                    console.error('Error fetching paid codes:', error);
                    setAllPaidCodes([]);
                    setCodeSuggestions([]);
                }
            };
            fetchPaidCodes();
        }
    }, [open]);

    // Filter paid codes based on selected period
    const getAvailablePaidCodes = useCallback(() => {
        if (!allPaidCodes.length) return [];
        
        return allPaidCodes.filter(paidCode => {
            // Check if this paid code number is used in any existing code
            // that overlaps with the selected period
            const existingCodes = overlappingPeriods.filter(period => 
                period.code_value.endsWith(paidCode.code)
            );
            return existingCodes.length === 0;
        });
    }, [allPaidCodes, overlappingPeriods]);

    // Modify handleNext to be simpler and only handle step progression
    const handleNext = () => {
        if (!validateCurrentStep()) return;
        setActiveStep((prevStep) => prevStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevStep) => prevStep - 1);
    };

    const handlePaidCodesFetch = async () => {
        try {
            setIsLoadingSuggestions(true);
            const response = await getPaidCodes(
                codeForm.valid_from.toISOString(),
                codeForm.valid_until.toISOString()
            );
            setCodeSuggestions(response || []);
        } catch (error) {
            console.error('Error fetching paid codes:', error);
            setCodeSuggestions([]);
        } finally {
            setIsLoadingSuggestions(false);
        }
    };

    const validateCurrentStep = () => {
        const errors = {};
        switch (activeStep) {
            case 0: // Guide
                return true;
            case 1: // URL
                // URL validation
                //if (!codeForm.code_url) {
                //    errors.code_url = 'URL is required';
                //}
                break;
            case 2: // Period
                if (!codeForm.valid_from || !codeForm.valid_until) {
                    errors.dates = 'Both dates are required';
                } else if (isBefore(new Date(codeForm.valid_until), new Date(codeForm.valid_from))) {
                    errors.dates = 'End date must be after start date';
                } else if (!editingCode && isAfter(new Date(codeForm.valid_until), addMonths(new Date(codeForm.valid_from), MAX_RENTAL_MONTHS))) {
                    errors.dates = `Maximum rental period is ${MAX_RENTAL_MONTHS} months`;
                }
                break;
            case 3: // Choose Code
                if (!selectedCode || !codeForm.code_value) {
                    errors.code_value = 'Please select a valid code';
                }
                break;
            case 4: // Add Details
                if (!codeForm.code_name.trim()) {
                    errors.code_name = 'Code name is required';
                }
                if (!codeForm.code_description.trim()) {
                    errors.code_description = 'Code description is required';
                }
                break;
            case 5: // Add Image
                // No validation required for image
                break;
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleUrlChange = (e) => {
        const url = e.target.value;
        setCodeForm(prev => ({ ...prev, code_url: url }));
        
        // Reset validation status when URL changes
        setValidationStatus({
            format: { status: 'inactive' },
            preview: { status: 'inactive' },
            existing: { status: 'inactive' }
        });
        
        if (formErrors.code_url) {
            setFormErrors(prev => ({ ...prev, code_url: '' }));
        }
    };

    // Revert to original validation logic
    const shouldStartValidation = (url) => {
        if (!url) return false;
        
        try {
            // Remove protocol if present
            const urlNoProtocol = url.replace(/^(https?:\/\/)?(www\.)?/, '');
            
            // Split into parts
            const parts = urlNoProtocol.split('.');
            
            // Basic format validation rules:
            // 1. Must have at least domain and TLD (2 parts)
            // 2. TLD must be 2-6 characters
            // 3. Domain part must be at least 1 character
            // 4. If starts with 'www', it must be exactly 3 'w's
            if (url.toLowerCase().startsWith('www')) {
                if (!/^www\./i.test(url)) return false;
            }
            
            return parts.length >= 2 && 
                   parts[parts.length - 1].length >= 2 &&
                   parts[parts.length - 1].length <= 6 &&
                   parts[0].length >= 1;
        } catch (e) {
            return false;
        }
    };

    // Update the URL validation effect to set pending status before API call
    useEffect(() => {
        const validateUrlWithDelay = async () => {
            if (!codeForm.code_url) {
                setValidationStatus({
                    format: { status: 'inactive' },
                    preview: { status: 'inactive' },
                    existing: { status: 'inactive' }
                });
                return;
            }

            const canStartValidation = shouldStartValidation(codeForm.code_url);
            const baseMessage = 'Waiting for valid URL...';

            // First set the initial status
            setValidationStatus({
                format: {
                    status: canStartValidation ? 'success' : 'error',
                    message: canStartValidation ? 'Valid URL format' : 'Invalid URL format'
                },
                preview: {
                    status: canStartValidation ? 'pending' : 'inactive',  // Set to pending when validation starts
                    message: canStartValidation ? 'Generating preview...' : baseMessage
                },
                existing: {
                    status: canStartValidation ? 'pending' : 'inactive',  // Set to pending when validation starts
                    message: canStartValidation ? 'Checking existing codes...' : baseMessage
                }
            });

            if (!canStartValidation) return;

            try {
                const result = await validateUrl(codeForm.code_url);
                setValidationStatus(prev => ({
                    ...prev,
                    preview: result.validations.preview,
                    existing: result.validations.existing
                }));
            } catch (error) {
                setValidationStatus(prev => ({
                    ...prev,
                    preview: { status: 'error', message: 'Failed to generate preview' },
                    existing: { status: 'error', message: 'Failed to check existing codes' }
                }));
            }
        };

        const timeoutId = setTimeout(validateUrlWithDelay, 1000);
        return () => clearTimeout(timeoutId);
    }, [codeForm.code_url]);

    // Effect for handling returns to Code step
    useEffect(() => {
        if (activeStep === 3) {
            // Reset validation state when entering step
            setCodeAvailability({
                isAvailable: false,
                overlappingPeriods: []
            });
            setCheckingAvailability(false);

            // If code is complete, validate with current dates
            if (CODE_REGEX.test(codeInput)) {
                checkCodeAvailability(codeInput);
            }
        }
    }, [activeStep]); // Only depend on step changes

    // Separate effect for handling code/date changes
    useEffect(() => {
        if (activeStep === 3 && CODE_REGEX.test(codeInput)) {
            checkCodeAvailability(codeInput);
        }
    }, [codeInput, codeForm.valid_from, codeForm.valid_until]); // Don't include activeStep

    const getRandomCodeFromCategory = (codes, categoryId) => {
        const categoryCodes = codes.filter(code => code.category_id === categoryId);
        if (categoryCodes.length === 0) return null;
        return categoryCodes[Math.floor(Math.random() * categoryCodes.length)];
    };

    const checkOverlappingCodes = async (code) => {
        try {
            const response = await checkCodeAvailabilityApi({
                code,
                valid_from: codeForm.valid_from,
                valid_until: codeForm.valid_until,
                exclude_code_id: editingCode?.code_id
            });
            console.log('AvailabilityApi from checkOverlappingCodes:', response);
            setOverlappingCode(response.overlappingCode || null);
            return !response.overlappingCode;

        } catch (error) {
            console.error('1: Error checking code availability:', error);
            return false;
        }
    };

    // Update generateCodeSuggestions to use new endpoint
    const generateCodeSuggestions = useCallback(async (input) => {
        if (!input) return { paid: [], free: [] };
        
        const letterPrefix = input.replace(/[^A-Z]/g, '');
        const numberPart = input.replace(/[A-Z]/g, '');
        
        if (letterPrefix.length < 2) return { paid: [], free: [] };

        try {
            // Get existing codes using the new endpoint
            const { suggestions: conflictingCodes } = await getCodeSuggestions({
                prefix: letterPrefix,
                valid_from: codeForm.valid_from,
                valid_until: codeForm.valid_until
            });

            const conflictingSet = new Set(conflictingCodes);

            // Filter paid codes that match current input pattern
            const matchingPaid = allPaidCodes
                .filter(paidCode => {
                    const codeNumber = paidCode.code?.toString().padStart(4, '0') || '';
                    const fullCode = `${letterPrefix}${codeNumber}`;
                    
                    // Check if code matches the number part if it exists
                    if (numberPart && !codeNumber.startsWith(numberPart)) {
                        return false;
                    }
                    
                    return !conflictingSet.has(fullCode);
                })
                .slice(0, 3) // Take top 3 paid suggestions
                .map(paidCode => ({
                    value: `${letterPrefix}${paidCode.code.toString().padStart(4, '0')}`,
                    isPaid: true,
                    price: parseFloat(paidCode.price) || 50
                }));

            // Generate free suggestions that match the input pattern
            const freeSuggestions = [];
            let count = 0;
            let num = 0;

            while (freeSuggestions.length < 5 && num < 10000) {
                const numStr = num.toString().padStart(4, '0');
                
                // Skip if it doesn't match the number part pattern
                if (numberPart && !numStr.startsWith(numberPart)) {
                    num++;
                    continue;
                }

                const fullCode = `${letterPrefix}${numStr}`;
                
                // Check if this code is available (not conflicting and not a paid code)
                if (!conflictingSet.has(fullCode) && 
                    !allPaidCodes.some(pc => pc.code?.toString().padStart(4, '0') === numStr)) {
                    freeSuggestions.push({
                        value: fullCode,
                        isPaid: false
                    });
                }
                
                num++;
            }

            return {
                paid: matchingPaid,
                free: freeSuggestions
            };
        } catch (error) {
            console.error('Error generating code suggestions:', error);
            return { paid: [], free: [] };
        }
    }, [allPaidCodes, codeForm]);

    // Separate function for availability check
    const checkCodeAvailability = async (code) => {
        setCheckingAvailability(true);
        setIsPriceChecking(true); // Add this
        try {
            const result = await checkCodeAvailabilityApi({
                code: code,
                valid_from: codeForm.valid_from,
                valid_until: codeForm.valid_until,
                exclude_code_id: editingCode?.code_id
            });
            setCodeAvailability(result);
          //  console.log('AvailabilityApi from checkCodeAvailability:', result);

            if (result.isAvailable) {
                const codeNumbers = code.slice(-4);
                const paidCode = allPaidCodes.find(pc => pc.code === codeNumbers);
                
                const codeData = {
                    code_value: code,
                    paid_code: paidCode
                };
                setSelectedCode(codeData);
                setCodeForm(prev => ({
                    ...prev,
                    code_value: code
                }));

                if (paidCode || (!editingCode && freeCodesRemaining <= 0)) {
                    setRequiresPayment(true);
                   console.log('checkCOdeAvailability 1:', codeData, incrementalPrice, paidCode?.category_id);
                    setPaymentDetails({
                        amount: paidCode ? Math.round(parseFloat(paidCode.price) * 100) : (incrementalPrice || 33),
                        reason: paidCode ? 'PAID_CODE' : 'INCREMENTAL',
                        categoryId: paidCode?.category_id
                    });
                    console.log('checkCOdeAvailability 2:', {
                        amount: paidCode ? Math.round(parseFloat(paidCode.price) * 100) : (incrementalPrice || 33),
                        reason: paidCode ? 'PAID_CODE' : 'INCREMENTAL',
                        categoryId: paidCode?.category_id,
                        // Add raw values for debugging
                        paidCode,
                        incrementalPrice,
                        'paidCode?.price': paidCode?.price
                    });
                    //      setShowPaymentModal(true);
                } else {
                    // Reset payment details when switching to a free code
                    setRequiresPayment(false);
                    setPaymentDetails(null);
                }
            } else {
                setSelectedCode(null);
            }
        } catch (error) {
            console.error('2: Error checking code availability:', error);
            setCodeAvailability({
                isAvailable: false,
                overlappingPeriods: []
            });
            setSelectedCode(null);
        } finally {
            setCheckingAvailability(false);
            setIsPriceChecking(false); // Add this
        }
    };

    const handleImageReady = async (processedImage) => {
        try {
            // Create local preview URL for the processed image
            const processedPreviewUrl = URL.createObjectURL(processedImage);
            
            // Store processed image for later S3 upload during CREATE
            setProcessedImage(processedImage);
            
            // Update preview with processed image
            setPreviewUrl(processedPreviewUrl);
            
            // Clear crop as we're done with it
            setCrop(null);
            
            // Enable CREATE button and disable UPLOAD button
            setIsImageUploaded(true);
            
            // Don't upload to S3 here - move it to handleSubmit
        } catch (error) {
            console.error('Error processing image:', error);
            setIsImageUploaded(false);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        if (file) {
            await handleImageSelect(file);
        }
    };

    const handleFileSelect = async (e) => {
        const file = e.target.files[0];
        if (file) {
            await handleImageSelect(file);
        }
    };

    const handleImageSelect = async (file) => {
        if (!file || !file.type.startsWith('image/')) {
            setFormErrors(prev => ({
                ...prev,
                image: 'Please select a valid image file'
            }));
            return;
        }
        
        try {
            // Optimize image
            const optimizedFile = await optimizeImage(file);
            setImageFile(optimizedFile);

            // Create and set preview
            const preview = await createImagePreview(optimizedFile);
            setPreviewUrl(preview);

            // Calculate and set initial crop
            const initialCrop = await calculateInitialCrop(preview);
            setCrop(initialCrop);

        } catch (error) {
            console.error('Error processing image:', error);
            setFormErrors(prev => ({
                ...prev,
                image: 'Error processing image'
            }));
        }
    };

    const handleCropComplete = async (crop) => {
        if (!previewUrl || !crop.width || !crop.height) return;

        try {
            const croppedBlob = await getCroppedImage(previewUrl, crop);
            setImageFile(croppedBlob);
            // Reset upload status when crop changes
            setIsImageUploaded(false);
        } catch (error) {
            console.error('Error cropping image:', error);
        }
    };

    const renderStepContent = () => {
        // Extract letter prefix and number part here since they're used in rendering
        const letterPrefix = codeInput.replace(/[0-9]/g, '');
        const numberPart = codeInput.replace(/[A-Z]/g, '');
        switch (activeStep) {
            case 0:
                return (
                    <Box>
                        <Typography variant="h5" sx={{ 
                            mt: { xs: 1, sm: 0 },
                            mb: { xs: 1, sm: 1 },
                            fontSize: {
                                xs: '1.25rem',  // Smaller font on mobile
                                sm: '1.5rem'    // Original h5 size on larger screens
                            }
                        }}>
                            Before we start...
                        </Typography>
                        <Typography variant="subtitle1" color="primary.main" gutterBottom
                         sx={{ mb: 0,
                            fontSize: {
                                xs: '1.0rem',  // Smaller font on mobile
                                sm: '1.5rem'    // Original h5 size on larger screens
                            }
                         }}>
                             FORMAT
                        </Typography>
                        <Typography variant="h5" sx={{ 
                            mb: 1,
                            fontSize: {
                                xs: '1.0rem',  // Smaller font on mobile
                                sm: '1.5rem'    // Original h5 size on larger screens
                            }
                        }}>
                            We reccomend to use this format: KL0101 or LON3003</Typography>
                        <Typography variant="subtitle1" color="primary.main" gutterBottom
                         sx={{ mb: 0,
                            fontSize: {
                                xs: '1.0rem',  // Smaller font on mobile
                                sm: '1.2rem'    // Original h5 size on larger screens
                            }
                         }}>
                            First 2 or 3 Letters
                        </Typography>
                        <Typography variant="body2"
                        sx={{ mb: 1,
                            fontSize: {
                                xs: '0.8rem',  // Smaller font on mobile
                                sm: '1.0rem'    // Original h5 size on larger screens
                            }
                         }}>
                            Choose a country (e.g., NL, US) or city (KL, NY, AMS, LON) prefix to make your code easy to remember for local users.
                        </Typography>
                        <Typography variant="subtitle1" color="primary.main" gutterBottom
                        sx={{ mb: 0,
                            fontSize: {
                                xs: '1.0rem',  // Smaller font on mobile
                                sm: '1.2rem'    // Original h5 size on larger screens
                            }
                         }}>
                              Last 4 Digits
                        </Typography>
                        <Typography variant="body2"
                        sx={{ mb: 1,
                            fontSize: {
                                xs: '0.8rem',  // Smaller font on mobile
                                sm: '1.0rem'    // Original h5 size on larger screens
                            }
                         }}>
                          • Designed to be easy to remember at a glance or after hearing it once.<br/>
                          • Select from thousands of free numbers or opt for premium sequences (e.g., xx0101, xxx7777) for a small fee.
                        </Typography>
                        <Typography variant="subtitle1" color="primary.main" gutterBottom
                         sx={{ mb: 0 }}>
                             DURATION
                        </Typography>
                        <Typography variant="body2"
                        sx={{ mb: { xs: 1, sm: 2 },
                            fontSize: {
                                xs: '0.8rem',  // Smaller font on mobile
                                sm: '1.0rem'    // Original h5 size on larger screens
                            }
                         }}>
                           • Valid for up to 3 months from its start date<br/>
                           • Renew your code to keep it active long-term<br/>
                           • Set a future activation date to ensure your code starts working exactly when you need it
                        </Typography>
                        <Typography variant="subtitle1" color="primary.main" gutterBottom
                         sx={{ mb: 0 }}>
                           FEES
                        </Typography>
                        <Typography variant="body2"
                        sx={{ mb: 0,
                            fontSize: {
                                xs: '0.8rem',  // Smaller font on mobile
                                sm: '1.0rem'    // Original h5 size on larger screens
                            }
                         }}>
                            • Get your first 3 Codes for free<br/>
                            • Link your free Codes to a single URL and use them across different locations
                            Example: NY3322, LA3322, and CH3322 can all direct to the same web page
                        </Typography>
                    </Box>
                );
            case 1:
                return (
                    <Box>
                        <Typography variant="h5" sx={{ mb: 3,
                            color: 'text.primary',
                            fontSize: {
                                xs: '1.25rem',  // Smaller font on mobile
                                sm: '1.5rem'    // Original h5 size on larger screens
                            }
                         }}>
                            Please enter the web page you want your Code to refer
                        </Typography>
                        
                        <StyledTextField
                            fullWidth
                            label="URL"
                            value={codeForm.code_url}
                            onChange={handleUrlChange}
                            error={!!formErrors.code_url}
                            helperText={formErrors.code_url}
                            placeholder="www.example.com"
                            InputProps={{
                                sx: { 
                                    py: 0,
                                    fontSize: {
                                        xs: '1.0rem',
                                        sm: '1.1rem'
                                    }
                                }
                            }}
                        />

                        <Box sx={{ 
                            mt: 3,
                            display: 'flex',
                            flexDirection: 'column',
                            p: { xs: 1.5, sm: 2.5 },
                            borderRadius: '16px',
                            backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(15, 23, 42, 0.02)',
                            border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(15, 23, 42, 0.06)'}`,
                            flex: 1
                         }}>
                            <ValidationLine 
                                label="URL Format"
                                status={!codeForm.code_url ? 'inactive' : validationStatus.format.status}
                                content={
                                    <Typography 
                                        component="span"
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: { xs: '0.85rem', sm: '0.95rem' },
                                            color: !codeForm.code_url ? 'text.disabled' : 
                                                   validationStatus.format.status === 'success' ? '#10B981' :
                                                   validationStatus.format.status === 'error' ? '#EF4444' :
                                                   'text.primary'
                                        }}
                                    >
                                        {!codeForm.code_url ? 'Waiting for URL input...' : validationStatus.format.message}
                                    </Typography>
                                }
                            />

                            <ValidationLine 
                                label="URL Preview"
                                status={!codeForm.code_url ? 'inactive' : validationStatus.preview.status}
                                content={
                                    validationStatus.preview.status === 'pending' ? (
                                        <Typography color="text.secondary" sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem' }, fontWeight: 500 }}>
                                            {validationStatus.preview.message}
                                        </Typography>
                                    ) : validationStatus.preview.screenshot ? (
                                        <Card sx={{ 
                                            width: 140, 
                                            height: 90, 
                                            flexShrink: 0,
                                            borderRadius: '10px',
                                            overflow: 'hidden',
                                            border: (theme) => `1px solid ${theme.palette.divider}`,
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                        }}>
                                            <CardMedia
                                                component="img"
                                                image={validationStatus.preview.screenshot}
                                                alt="Website preview"
                                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                        </Card>
                                    ) : (
                                        <Typography sx={{ 
                                            fontSize: { xs: '0.85rem', sm: '0.95rem' },
                                            fontWeight: 500,
                                            color: !codeForm.code_url ? 'text.disabled' : 
                                                   validationStatus.preview.status === 'error' ? '#EF4444' : 'text.primary'
                                        }}>
                                            {validationStatus.preview.message}
                                        </Typography>
                                    )
                                }
                            />

                            <ValidationLine 
                                label="Existing Codes"
                                status={!codeForm.code_url ? 'inactive' : validationStatus.existing.status}
                                content={
                                    validationStatus.existing.codes?.length > 0 ? (
                                        <Box sx={{ 
                                            maxHeight: { xs: 230, sm: 200 }, 
                                            width: '100%',
                                            overflowY: 'auto',
                                            '&::-webkit-scrollbar': {
                                                width: '6px'
                                            },
                                            '&::-webkit-scrollbar-thumb': {
                                                backgroundColor: 'action.hover',
                                                borderRadius: '3px'
                                            }
                                        }}>
                                            {validationStatus.existing.codes.map((code, index) => (
                                                <Box key={index} sx={{ 
                                                    display: 'flex', 
                                                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                                                    alignItems: 'center', 
                                                    gap: 1.5,
                                                    mb: 1,
                                                    p: 1,
                                                    borderRadius: '10px',
                                                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#FFFFFF',
                                                    border: (theme) => `1px solid ${theme.palette.divider}`
                                                }}>
                                                    <Box sx={{ 
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1.5,
                                                        minWidth: { xs: '100%', sm: 'auto' }
                                                    }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace', letterSpacing: '0.04em' }}>
                                                            {code.code_value}
                                                        </Typography>
                                                        <Chip 
                                                            label={code.status}
                                                            size="small"
                                                            sx={{
                                                                borderRadius: '20px',
                                                                px: 0.5,
                                                                fontSize: '0.72rem',
                                                                fontWeight: 700,
                                                                letterSpacing: '0.02em',
                                                                backgroundColor: 
                                                                    code.status === 'ACTIVE' ? 'rgba(16, 185, 129, 0.15)' :
                                                                    code.status === 'RESERVED' ? 'rgba(0, 102, 255, 0.15)' :
                                                                    'rgba(148, 163, 184, 0.15)',
                                                                color: 
                                                                    code.status === 'ACTIVE' ? '#10B981' :
                                                                    code.status === 'RESERVED' ? '#0066FF' :
                                                                    '#94A3B8',
                                                                border: (theme) => `1px solid ${
                                                                    code.status === 'ACTIVE' ? 'rgba(16, 185, 129, 0.3)' :
                                                                    code.status === 'RESERVED' ? 'rgba(0, 102, 255, 0.3)' :
                                                                    'rgba(148, 163, 184, 0.3)'
                                                                }`
                                                            }}
                                                        />
                                                    </Box>
                                                    <Typography 
                                                        variant="caption" 
                                                        color="text.secondary"
                                                        sx={{ fontWeight: 500 }}
                                                    >
                                                        ({format(new Date(code.valid_from), 'dd-MMM-yyyy')} - 
                                                        {format(new Date(code.valid_until), 'dd-MMM-yyyy')})
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    ) : (
                                        <Typography 
                                            sx={{
                                                fontSize: { xs: '0.85rem', sm: '0.95rem' },
                                                fontWeight: 500,
                                                color: !codeForm.code_url ? 'text.disabled' : 
                                                       validationStatus.existing.status === 'error' ? '#EF4444' : 'text.primary'
                                            }}
                                        >
                                            {validationStatus.existing.message}
                                        </Typography>
                                    )
                                }
                            />
                        </Box>
                    </Box>
                );
            case 2:
                return (
                    <Box>
                        <Typography variant="h5" sx={{ mb: 3,
                            color: 'text.primary',
                            fontSize: {
                                xs: '1.25rem',  // Smaller font on mobile
                                sm: '1.5rem'    // Original h5 size on larger screens
                            }
                         }}>
                            {editingCode ? 'Code Period' : 'Select Code Period'}
                        </Typography>
                        
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <Box sx={{ mt: 4 }}>
                                {formErrors.dates && (
                                    <Alert severity="error" sx={{ mb: 2 }}>{formErrors.dates}</Alert>
                                )}
                                <Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: 'column',
                                    gap: 3
                                }}>
                                    <Box>
                                        <Typography 
                                            variant="caption" 
                                            sx={{ 
                                                color: 'text.secondary',
                                                mb: 1,
                                                display: 'block'
                                            }}
                                        >
                                            Valid From
                                        </Typography>
                                        <DatePicker
                                            open={fromPickerOpen}
                                            onOpen={() => setFromPickerOpen(true)}
                                            onClose={() => setFromPickerOpen(false)}
                                            value={codeForm.valid_from}
                                            onChange={(date) => {
                                                if (!date || isNaN(new Date(date).getTime())) return;
                                                setCodeForm(prev => ({ 
                                                    ...prev, 
                                                    valid_from: date,
                                                    valid_until: (prev.valid_until && isBefore(new Date(prev.valid_until), new Date(date)))
                                                        ? (editingCode ? addMonths(date, 3) : addDays(date, 92))
                                                        : prev.valid_until
                                                }));
                                            }}
                                            minDate={editingCode && codeForm.valid_from && isBefore(new Date(codeForm.valid_from), new Date()) ? new Date(codeForm.valid_from) : new Date()}
                                            maxDate={editingCode ? addYears(new Date(), 5) : addMonths(new Date(), 1)}
                                            inputFormat="dd-MMM-yyyy"
                                            renderInput={(params) => (
                                                <StyledDateField
                                                    {...params}
                                                    fullWidth
                                                    onClick={() => setFromPickerOpen(true)}
                                                    sx={{
                                                        cursor: 'pointer',
                                                        '& .MuiInputBase-root': { cursor: 'pointer' },
                                                        '& .MuiInputBase-input': { cursor: 'pointer' }
                                                    }}
                                                />
                                            )}
                                        />
                                    </Box>
                                    <Box>
                                        <Typography 
                                            variant="caption" 
                                            sx={{ 
                                                color: 'text.secondary',
                                                mb: 1,
                                                display: 'block'
                                            }}
                                        >
                                            Valid Until
                                        </Typography>
                                        <DatePicker
                                            open={untilPickerOpen}
                                            onOpen={() => setUntilPickerOpen(true)}
                                            onClose={() => setUntilPickerOpen(false)}
                                            value={codeForm.valid_until}
                                            onChange={(date) => {
                                                if (!date || isNaN(new Date(date).getTime())) return;
                                                setCodeForm(prev => ({ ...prev, valid_until: date }));
                                            }}
                                            minDate={codeForm.valid_from ? new Date(codeForm.valid_from) : new Date()}
                                            maxDate={editingCode ? addYears(new Date(), 5) : addDays(codeForm.valid_from ? new Date(codeForm.valid_from) : new Date(), 92)}
                                            inputFormat="dd-MMM-yyyy"
                                            renderInput={(params) => (
                                                <StyledDateField
                                                    {...params}
                                                    fullWidth
                                                    onClick={() => setUntilPickerOpen(true)}
                                                    sx={{
                                                        cursor: 'pointer',
                                                        '& .MuiInputBase-root': { cursor: 'pointer' },
                                                        '& .MuiInputBase-input': { cursor: 'pointer' }
                                                    }}
                                                />
                                            )}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </LocalizationProvider>
                    </Box>
                );
            case 3:
                return (
                    <Box>
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: { xs: 1, sm: 2 }
                        }}>
                            {/* Left section - Code Input */}
                            <Box sx={{
                                flex: { xs: '1', sm: '0 0 250px' }
                            }}>
                                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                    Enter Code
                                </Typography>
                                <StyledTextField
                                    fullWidth
                                    label="Code"
                                    value={codeInput}
                                    onChange={(e) => handleCodeInput(e.target.value)}
                                    error={!!formErrors.code_value}
                                    placeholder="NL0000 or AMS0000"
                                    autoComplete="off"
                                    inputProps={{
                                        maxLength: 7,
                                        style: { textTransform: 'uppercase' },
                                        readOnly: !!editingCode // Add this line to make it read-only in edit mode
                                    }}
                                />
                                {/* Flash validation message moved below input */}
                                {codeInput.length === 1 && (
                                    <Typography 
                                        variant="body2" 
                                        color="text.secondary"
                                        sx={{ 
                                            mt: 1,
                                            mb: 1,
                                            animation: 'fadeIn 0.3s ease-in',
                                            '@keyframes fadeIn': {
                                                '0%': {
                                                    opacity: 0,
                                                    transform: 'translateY(5px)'
                                                },
                                                '100%': {
                                                    opacity: 1,
                                                    transform: 'translateY(0)'
                                                }
                                            }
                                        }}
                                    >
                                        {getValidationMessage(codeInput)}
                                    </Typography>
                                )}
                                {showSuggestions && (
                                    <CodeSuggestions
                                        validationMessage={getValidationMessage(codeInput)}
                                        suggestions={filteredSuggestions}
                                        onSelect={(value) => {
                                            setCodeInput(value);
                                            setShowSuggestions(false);
                                        }}
                                        freeCodesRemaining={freeCodesRemaining}
                                        incrementalPrice={incrementalPrice}
                                    />
                                )}
                            </Box>

                            {/* Right section - Price/Details */}
                            <Box sx={{
                                flex: { xs: '1', sm: 1 }
                            }}>
                                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                    Price/Details
                                    {freeCodesRemaining !== null && (
                                        <Typography 
                                            component="span" 
                                            color="text.secondary"
                                            sx={{ ml: 1 }}
                                        >
                                            ({freeCodesRemaining} free codes remaining)
                                        </Typography>
                                    )}
                                </Typography>
                                <Box sx={{ 
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    alignItems: { xs: 'flex-start', sm: 'center' },
                                    gap: { xs: 2, sm: 3 },
                                    minHeight: '56px'
                                }}>
                                    {getValidationMessage(codeInput) === 'Valid code format' && (
                                        <>
                                            {/* Price display */}
                                            {isPriceChecking ? (
                                                <CircularProgress size={20} />
                                            ) : (
                                                selectedCode && (
                                                    <Typography>
                                                        ${selectedCode.paid_code ? 
                                                            selectedCode.paid_code.price : 
                                                            (!editingCode && freeCodesRemaining <= 0) ? 
                                                                (incrementalPrice / 100).toFixed(2) : 
                                                                '0'
                                                        }
                                                    </Typography>
                                                )
                                            )}
                                            {!codeAvailability.overlappingPeriods?.length && !checkingAvailability && (
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center',
                                                    gap: 1
                                                }}>
                                                    <CheckCircleIcon color="success" />
                                                    <Typography sx={{ color: theme => theme.palette.success.main }}>
                                                        Code is available for selected period
                                                    </Typography>
                                                </Box>
                                            )}
                                        </>
                                    )}
                                </Box>

                                {/* Conflict Messages */}
                                {codeAvailability.overlappingPeriods?.length > 0 && (
                                    <Box sx={{ 
                                        mt: { xs: 1, sm: 2 },
                                        width: '100%' // Full width on both mobile and desktop
                                    }}>
                                        <Typography color="error.main" sx={{ mb: 1 }}>
                                            {editingCode 
                                                ? 'Code is already in use. Please choose different Code value'
                                                : 'Code is already in use. Please change dates or Code value'
                                            }
                                        </Typography>
                                        <Box sx={{ 
                                            gap: { xs: 1, sm: 1 },
                                            display: 'flex', 
                                            flexDirection: 'column',
                                            width: '100%'
                                        }}>
                                            {codeAvailability.overlappingPeriods.map((period, index) => (
                                                <Box 
                                                    key={index} 
                                                    sx={{ 
                                                        p: { xs: 0, sm: 0 },
                                                        width: '100%',
                                                        '&:not(:last-child)': {
                                                            borderBottom: '1px solid',
                                                            borderColor: 'divider'
                                                        }
                                                    }}
                                                >
                                                    <Typography variant="body2" sx={{ 
                                                        display: 'flex',
                                                        flexDirection: { xs: 'column', sm: 'row' },
                                                        gap: { xs: 0, sm: 1 },
                                                        color: theme => theme.palette.text.primary
                                                    }}>
                                                        <span>
                                                            {format(new Date(period.valid_from), 'MMM dd, yyyy')} - {format(new Date(period.valid_until), 'MMM dd, yyyy')}
                                                        </span>
                                                        <Box 
                                                            component="span" 
                                                            sx={{ 
                                                                display: { xs: 'none', sm: 'inline' },
                                                                color: theme => theme.palette.text.secondary 
                                                            }}
                                                        >
                                                            /
                                                        </Box>
                                                        <span sx={{ color: theme => theme.palette.text.secondary }}>
                                                            {period.code_url}
                                                        </span>
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Box>
                );
            case 4:
                return (
                    <Box>
                        <Typography variant="h6" sx={{ mb: 3 }}>
                            Add Details
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <TextField
                                fullWidth
                                label="Code Name"
                                value={codeForm.code_name}
                                onChange={(e) => {
                                    const value = e.target.value.slice(0, 20); // Limit to 20 chars
                                    setCodeForm(prev => ({ ...prev, code_name: value }));
                                }}
                                error={!!formErrors.code_name}
                                helperText={formErrors.code_name || `${codeForm.code_name.length}/20 characters`}
                                placeholder="Enter a name for your code"
                            />
                            <TextField
                                fullWidth
                                label="Code Description"
                                value={codeForm.code_description}
                                onChange={(e) => {
                                    const value = e.target.value.slice(0, 120); // Limit to 120 chars
                                    setCodeForm(prev => ({ ...prev, code_description: value }));
                                }}
                                error={!!formErrors.code_description}
                                helperText={formErrors.code_description || `${codeForm.code_description.length}/120 characters`}
                                placeholder="Describe what this code is for"
                                multiline
                                rows={4}
                            />
                        </Box>
                    </Box>
                );
            case 5:
                return (
                    <Box>
                        <Typography variant="h6" sx={{ mb: 3 }}>
                            Add Image
                        </Typography>
                        <Box sx={{ 
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2
                        }}>
                            {!previewUrl ? (
                                <DropZone
                                    isDragging={isDragging}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                    sx={{
                                        height: {
                                            xs: '250px', // Shorter height on mobile
                                            sm: '400px'  // Original height on desktop
                                        }
                                    }}
                                >
                                    <CloudUploadIcon sx={{ fontSize: 48, mb: 2, color: 'text.secondary' }} />
                                    <Typography variant="body1" color="text.secondary" mb={2}>
                                        Drag and drop an image here, or
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        component="label"
                                    >
                                        Select from Files
                                        <input
                                            type="file"
                                            hidden
                                            accept="image/*"
                                            onChange={(e) => handleImageSelect(e.target.files[0])}
                                        />
                                    </Button>
                                </DropZone>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box sx={{ 
                                        height: {
                                            xs: '250px', // Shorter height on mobile
                                            sm: '400px'  // Original height on desktop
                                        },
                                        overflow: 'hidden',
                                        borderRadius: 1,
                                        borderColor: 'divider',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        {!codeForm.image_url ? (
                                            <ReactCrop
                                                crop={crop}
                                                onChange={(c) => setCrop(c)}
                                                onComplete={handleCropComplete}
                                                aspect={1}
                                            >
                                                <img
                                                    src={previewUrl}
                                                    style={{
                                                        maxWidth: '100%',
                                                        maxHeight: '100%', // Changed to 100% to fit container
                                                        objectFit: 'contain'
                                                    }}
                                                    alt="Preview"
                                                />
                                            </ReactCrop>
                                        ) : (
                                            <img
                                                src={codeForm.image_url}
                                                style={{
                                                    maxWidth: '100%',
                                                    maxHeight: '100%', // Changed to 100% to fit container
                                                    objectFit: 'contain'
                                                }}
                                                alt="Final"
                                            />
                                        )}
                                    </Box>
                                    <Box sx={{ 
                                        display: 'flex', 
                                        gap: 2,
                                        justifyContent: 'flex-end',
                                        mt: { xs: 1, sm: 2 } // Reduced margin on mobile
                                    }}>
                                        <Button 
                                            onClick={handleClear}
                                            color="primary"
                                            variant="outlined"
                                        >
                                            CLEAR
                                        </Button>
                                        <Button 
                                            onClick={async () => {
                                                try {
                                                    const croppedBlob = await getCroppedImage(previewUrl, crop);
                                                    const optimizedFile = await optimizeImage(croppedBlob);
                                                    await handleImageReady(optimizedFile);
                                                } catch (error) {
                                                    console.error('Error processing image:', error);
                                                }
                                            }}
                                            color="primary"
                                            variant="contained"
                                            disabled={isImageUploaded}
                                        >
                                            UPLOAD
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Box>
                );
            // Add other steps...
            default:
                return null;
        }
    };

    const handleClear = () => {
        // Clean up old preview URL if exists
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
        }
        
        // Reset all image-related states
        setPreviewUrl(null);
        setImageFile(null);
        setCrop(null);
        setCompletedCrop(null);
        setProcessedImage(null);
        setIsImageUploaded(false);
        setCodeForm(prev => ({ 
            ...prev, 
            image_url: '',
            processedImage: null
        }));
    };

    // Modify handleCodeSelection to ensure state persistence
    const handleCodeSelection = (code) => {
        console.log('handleCodeSelection called with price:', incrementalPrice);
        setSelectedCode(code);
        
        const codeNumbers = code?.code_value?.slice(-4);
        const isPaidCode = allPaidCodes.some(pc => pc.code.toString() === codeNumbers.toString());
        const needsIncrementalPayment = !editingCode && freeCodesRemaining <= 0;

        if (isPaidCode || needsIncrementalPayment) {
            const paidCode = allPaidCodes.find(pc => pc.code.toString() === codeNumbers.toString());
            const paymentDetails = {
                amount: paidCode ? Math.round(parseFloat(paidCode.price) * 100) : (incrementalPrice || 33),
                reason: paidCode ? 'PAID_CODE' : 'INCREMENTAL',
                categoryId: paidCode?.category_id
            };
            console.log('Setting payment with price:', paymentDetails.amount);
            setPaymentDetails(paymentDetails);
            setRequiresPayment(true);
        }
    };

    // Add a helper function for image upload
    const uploadCodeImage = async (imageFile) => {
        if (!imageFile) return '';
        
        try {
            const formData = new FormData();
            formData.append('file', imageFile);
            const uploadResponse = await uploadImage(formData);
            return uploadResponse.url;
        } catch (error) {
            console.error('Error uploading image:', error);
            return '';
        }
    };

    // Update handleSubmit for free codes
    const handleSubmit = useCallback(async (isPaid = false) => {
        try {
            let finalCode;
            
            if (isPaid && tempCodeId) {
                finalCode = await convertTempCode(tempCodeId);
                setTempCodeId(null);
            } else {
                // Upload image if we have one - using original approach
                let imageUrl = '';
                if (imageFile) {
                    const uploadResponse = await uploadImage(imageFile, codeForm.image_url);
                    imageUrl = uploadResponse;
                }

                const codeData = {
                    code_value: codeForm.code_value,
                    code_url: codeForm.code_url,
                    valid_from: codeForm.valid_from,
                    valid_until: codeForm.valid_until,
                    code_name: codeForm.code_name,
                    code_description: codeForm.code_description,
                    is_paid: isPaid,
                    image_url: imageUrl || codeForm.image_url
                };

                if (editingCode) {
                    // Use updateCode for edit mode
                    finalCode = await updateCode(editingCode.code_id, codeData);
                } else {
                    // Use createCode for new codes
                    finalCode = await createCode(codeData);
                }
            }

            onCodeSaved(finalCode);
            onClose();
        } catch (error) {
            console.error('Error submitting code:', error);
            setError(error.message || 'Failed to submit code. Please try again.');
        }
    }, [tempCodeId, onClose, onCodeSaved, imageFile, codeForm, editingCode]);

    // Update payment success handler to use the same upload function
    const handlePaymentSuccess = async ({ paymentIntent, codeId }) => {
        try {
            console.log('Payment successful, converting temp code:', codeId);
            
            // Use original image upload approach
            let imageUrl = '';
            if (imageFile) {
                try {
                    // Upload the image first
                    const uploadResponse = await uploadImage(imageFile, codeForm.image_url);
                    imageUrl = uploadResponse; // This is the URL string
                    console.log('Image uploaded successfully:', imageUrl);
                } catch (uploadError) {
                    console.error('Error uploading image:', uploadError);
                    setError('Warning: Failed to upload image. Code will be created without an image.');
                }
            }

            // Convert temp code to permanent with image URL
            const response = await convertTempCode(codeId, paymentIntent.id, { 
                image_url: imageUrl
            });

            console.log('Temp code converted successfully:', response);
            
            // Clear error if success
            setError(null);
            
            // Close payment modal
            setShowPaymentModal(false);
            
            // Clear any timers
            if (paymentTimer) {
                clearInterval(paymentTimer);
                setPaymentTimer(null);
            }

            // Notify parent of success
            onCodeSaved(response);
            
            // Close the form
            onClose();
        } catch (error) {
            console.error('Error converting temp code:', error);
            setError('Payment successful but failed to finalize code. Please contact support.');
        }
    };

    const handleClose = () => {
        // Clean up any object URLs to prevent memory leaks
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
        }
        
        setCodeForm({
            code_url: '',
            valid_from: new Date(),
            valid_until: addMonths(new Date(), 1),
            code_value: '',
            code_name: '',
            code_description: '',
            image_url: '',
            processedImage: null
        });
        setProcessedImage(null);
        setPreviewUrl(null);
        setCrop(null);
        setFormErrors({});
        onClose();
    };

    // Update the isUrlValidationSuccessful function
    const isUrlValidationSuccessful = () => {
        return validationStatus.format?.status === 'success' && 
               validationStatus.preview?.status === 'success';
    };

    // Update title based on mode
    const getDialogTitle = () => {
        if (editingCode) {
            return `Edit Code: ${editingCode.code_value}`;
        }
        return 'Create New Code';
    };

    // Initialize form with existing values when in edit mode
    useEffect(() => {
        if (editingCode) {
            setCodeForm({
                code_url: editingCode.code_url || '',
                valid_from: editingCode.valid_from ? new Date(editingCode.valid_from) : null,
                valid_until: editingCode.valid_until ? new Date(editingCode.valid_until) : null,
                code_value: editingCode.code_value || '',
                code_name: editingCode.code_name || '',
                code_description: editingCode.code_description || '',
                image_url: editingCode.image_url ? `${editingCode.image_url}${editingCode.image_url.includes('?') ? '&' : '?'}v=${Date.now()}` : ''
            });

            // Pre-fill the code input in step 4
            if (editingCode.code_value) {
                setCodeInput(editingCode.code_value);
            }

            // Set up the image preview if image exists
            if (editingCode.image_url) {
                setPreviewUrl(`${editingCode.image_url}${editingCode.image_url.includes('?') ? '&' : '?'}v=${Date.now()}`);
                setIsImageUploaded(true);
            }
        }
    }, [editingCode]);

    // Update the next button disabled state
    const isNextButtonDisabled = () => {
        if (editingCode) {
            switch (activeStep) {
                case 1:
                    return !isUrlValidationSuccessful();
                case 2:
                case 3:
                    return false; // Always enabled in edit mode for these steps
                case 4:
                    return !codeForm.code_name || !codeForm.code_description;
                case 5:
                    return !isImageUploaded;
                default:
                    return false;
            }
        } else {
            return activeStep === 0 ? false :
                   activeStep === 1 ? !isUrlValidationSuccessful() :
                   activeStep === 2 ? !!formErrors.dates :
                   activeStep === 3 ? !selectedCode :
                   activeStep === 4 ? !codeForm.code_name || !codeForm.code_description :
                   activeStep === 5 ? !isImageUploaded :
                   false;
        }
    };

    // Update handleCodeInput function to clear error when code changes
    const handleCodeInput = async (value) => {
        // If current input is already a complete code and new value is longer, ignore the change
        if (CODE_REGEX.test(codeInput) && value.length > codeInput.length) {
            return;
        }

        const upperValue = value.toUpperCase();
        
        // Extract current letters and numbers from the previous input
        const prevLetters = codeInput.replace(/[^A-Z]/g, '');
        const prevNumbers = codeInput.replace(/[A-Z]/g, '');
        
        // Extract letters and numbers from new input
        let letters = upperValue.replace(/[^A-Z]/g, '');
        let numbers = upperValue.replace(/[A-Z]/g, '');
        
        // If we already had numbers and they're trying to add letters, keep the old letters
        if (prevNumbers.length > 0 && letters.length > prevLetters.length) {
            letters = prevLetters;
        }
        
        // Apply format rules
        letters = letters.slice(0, 3);  // Max 3 letters
        numbers = numbers.slice(0, 4);  // Max 4 numbers
        
        // Enforce format: letters first, then numbers
        let formattedValue = letters;
        if (numbers.length > 0) {
            // Only add numbers if we have at least 2 letters
            if (letters.length >= 2) {
                formattedValue += numbers;
            }
        }
        
        // If the formatted value hasn't changed, don't proceed with updates
        if (formattedValue === codeInput) {
            return;
        }
        
        // Clear error when code changes
        setError(null);
        
        // Reset validation states if code changes
        setCodeAvailability({
            overlappingPeriods: [],
            paidCode: null
        });
        setCheckingAvailability(false);
        
        // Reset payment state when code changes
        setRequiresPayment(false);
        setPaymentDetails(null);
        
        setCodeInput(formattedValue);
        
        // Show suggestions as soon as we have 2 letters, regardless of completion
        const shouldShowSuggestions = letters.length >= 2;
        setShowSuggestions(shouldShowSuggestions);
        
        // Generate suggestions when we have at least 2 letters
        if (shouldShowSuggestions) {
            setCheckingAvailability(true);
            try {
                const suggestions = await generateCodeSuggestions(formattedValue);
                setFilteredSuggestions(suggestions);
            } catch (error) {
                console.error('Error generating suggestions:', error);
                setFilteredSuggestions({ paid: [], free: [] });
            } finally {
                setCheckingAvailability(false);
            }
        } else {
            setFilteredSuggestions({ paid: [], free: [] });
        }
        
        // If code is complete (2-3 letters + 4 numbers), check availability
        if (CODE_REGEX.test(formattedValue)) {
            handleCheckAvailability(formattedValue);
        }
    };

    // Modify handleCheckAvailability to not trigger handleSubmit
    const handleCheckAvailability = useCallback(
        
        debounce((code) => {
           // console.log('handleCheckAvailability before if:', code, codeForm.valid_from, codeForm.valid_until);  // Add call stack trace
            if (CODE_REGEX.test(code)) {
                setCheckingAvailability(true);
                setIsAvailabilityCheck(true);
             //   console.log('handleCheckAvailability after if:', code, codeForm.valid_from, codeForm.valid_until);  // Add call stack trace
              
                // checkCodeAvailabilityApi(code, codeForm.valid_from, codeForm.valid_until)
              
               checkCodeAvailabilityApi({
                    code: code,
                    valid_from: codeForm.valid_from,
                    valid_until: codeForm.valid_until,
                    exclude_code_id: editingCode?.code_id
                })
                    .then(response => {
                        setCodeAvailability(response);
                     //   console.log('AvailabilityApi from handleCheckAvailability 1:', response);
                        // Fix the property name check
                    //    if (response.isAvailable) {  // Changed from available to isAvailable
                     //       console.log('AvailabilityApi from handleCheckAvailability 2:', code);
                       //     handleCodeSelection({
                     //           code_value: code
                     //       });
                      //  }
                    })
                    .catch(error => {
                        console.error('3: Error checking code availability:', error);
                    })
                    .finally(() => {
                        setCheckingAvailability(false);
                        setIsAvailabilityCheck(false);
                    });
            }
        }, 300),
        [codeForm.valid_from, codeForm.valid_until]
    );

    // Fetch free codes count on mount
    useEffect(() => {
        const fetchFreeCodesCount = async () => {
            try {
                const ownerDetails = await getCodeOwnerDetails();
                const totalFreeCodes = ownerDetails.free_codes;
                const activeCodesCount = ownerDetails.active_codes_count;
                setFreeCodesRemaining(Math.max(0, totalFreeCodes - activeCodesCount));
            } catch (error) {
                console.error('Error fetching free codes count:', error);
                setFreeCodesRemaining(0);
            }
        };

        fetchFreeCodesCount();
    }, []);

    // Add this effect to fetch incremental price
    useEffect(() => {
        const fetchIncrementalPrice = async () => {
            try {
                const params = await getParameters();
                const priceParam = params.find(p => p.parameter_name === 'incremental_price');
                // Convert price from dollars to cents for Stripe
                const priceInCents = Math.round(parseFloat(priceParam?.parameter_value || '0') * 100);
                setIncrementalPrice(priceInCents);
            } catch (error) {
                console.error('Error fetching incremental price:', error);
            }
        };

        fetchIncrementalPrice();
    }, []);

    // Add cleanup effect
    useEffect(() => {
        return () => {
            if (paymentTimer) {
                clearInterval(paymentTimer);
            }
        };
    }, [paymentTimer]);

    return (
        <Dialog 
            open={open} 
            onClose={handleDialogClose} 
            maxWidth="md" 
            fullWidth
            PaperProps={{
                sx: { 
                    height: { 
                        xs: '92%',
                        sm: '95%',
                        md: '820px'
                    },
                    maxHeight: {
                        xs: '92%',
                        sm: '95%',
                        md: '90vh'
                    },
                    width: {
                        xs: '92%',
                        sm: '95%',
                        md: '820px'
                    },
                    maxWidth: {
                        xs: '92%',
                        sm: '95%',
                        md: '820px'
                    },
                    margin: {
                        xs: 1,
                        sm: 2,
                        md: 3
                    },
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: {
                        xs: '16px',
                        sm: '24px'
                    },
                    border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(15, 23, 42, 0.1)'}`,
                    boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 24px 60px rgba(0,0,0,0.7)' : '0 20px 50px rgba(15,23,42,0.15)',
                    overflow: 'hidden'
                }
            }}
        >
            <DialogTitle sx={{ 
                pt: { xs: 2, sm: 3 },  
                pb: { xs: 1.5, sm: 2 },
                px: { xs: 2.5, sm: 4 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                typography: 'h5',
                color: 'text.primary',
                fontWeight: 700,
                letterSpacing: '-0.02em',
                fontSize: { 
                    xs: '1.25rem',
                    sm: '1.5rem'
                }
            }}>
                <Box component="span">{getDialogTitle()}</Box>
                <IconButton 
                    onClick={handleDialogClose}
                    size="small"
                    sx={{ 
                        color: 'text.secondary',
                        borderRadius: '10px',
                        '&:hover': { color: 'text.primary', backgroundColor: 'action.hover' }
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            
            <StyledStepper 
                activeStep={activeStep} 
                alternativeLabel 
                sx={{ 
                    px: { xs: 1, sm: 4 },
                    mb: { xs: 1.5, sm: 2.5 },
                    '& .MuiStepLabel-label': {
                        fontSize: {
                            xs: '0.75rem',
                            sm: '0.85rem'
                        }
                    }
                }}
            >
                {getSteps(editingCode).map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </StyledStepper>

            <DialogContent sx={{ 
                px: { xs: 1.5, sm: 4 },
                flex: 1,
                overflow: 'hidden',
                py: 0
            }}>
                <Box sx={{
                    bgcolor: (theme) => theme.palette.mode === 'dark' ? '#0E1524' : '#F8FAFC',
                    borderRadius: '16px',
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    px: { xs: 1.5, sm: 3 }, 
                    py: { xs: 1.5, sm: 3 },
                    height: '100%',
                    my: 0,
                    overflow: 'auto'
                }}>
                    {renderStepContent()}
                </Box>
            </DialogContent>

            <DialogActions sx={{ 
                px: { xs: 2, sm: 4 },
                py: { xs: 2, sm: 2.5 },
                justifyContent: 'space-between',
                borderTop: '1px solid',
                borderColor: 'divider',
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.01)' : '#FFFFFF'
            }}>
                <Button 
                    onClick={handleBack}
                    sx={{ 
                        visibility: activeStep > 0 ? 'visible' : 'hidden',
                        color: 'text.secondary',
                        borderRadius: '20px',
                        px: 2.5,
                        py: 0.8,
                        fontSize: {
                            xs: '0.85rem',
                            sm: '0.95rem'
                        },
                        fontWeight: 600,
                        textTransform: 'none',
                        '&:hover': {
                            backgroundColor: (theme) => theme.palette.action.hover
                        }
                    }}
                >
                    Back
                </Button>
                <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 } }}>
                    <Button 
                        onClick={onClose}
                        sx={{ 
                            color: 'text.secondary',
                            borderRadius: '20px',
                            px: 2.5,
                            py: 0.8,
                            fontSize: {
                                xs: '0.85rem',
                                sm: '0.95rem'
                            },
                            fontWeight: 600,
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: (theme) => theme.palette.action.hover
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="contained" 
                        onClick={activeStep === getSteps(editingCode).length - 1 ? handleFinalSubmit : handleNext}
                        disabled={isNextButtonDisabled()}
                        sx={{
                            borderRadius: '24px',
                            px: { xs: 3, sm: 4 },
                            py: 0.8,
                            fontSize: {
                                xs: '0.85rem',
                                sm: '0.95rem'
                            },
                            fontWeight: 600,
                            textTransform: 'none',
                            background: 'linear-gradient(135deg, #0066FF 0%, #0052CC 100%)',
                            color: '#FFFFFF',
                            boxShadow: '0 4px 14px rgba(0, 102, 255, 0.35)',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #0052CC 0%, #003D99 100%)',
                                boxShadow: '0 6px 20px rgba(0, 102, 255, 0.45)',
                                transform: 'translateY(-1px)'
                            },
                            '&.Mui-disabled': {
                                background: (theme) => theme.palette.action.disabledBackground,
                                color: (theme) => theme.palette.action.disabled,
                                boxShadow: 'none'
                            }
                        }}
                    >
                        {activeStep === getSteps(editingCode).length - 1 ? (editingCode ? 'Save' : 'Create') : 'Next'}
                    </Button>
                </Box>
            </DialogActions>

            {/* Add Payment Modal */}
            {showPaymentModal && (
                <Dialog
                    open={showPaymentModal}
                    onClose={handlePaymentModalClose}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center' 
                        }}>
                            <Typography>Payment Required</Typography>
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                bgcolor: paymentTimeLeft < 60 ? 'error.light' : 'background.paper',
                              //  color: paymentTimeLeft < 60 ? 'error.main' : 'text.primary',
                                borderRadius: 1,
                                px: 1.5,
                                py: 0.5,
                            }}>
                                <Typography variant="body2">
                                    {`${Math.floor(paymentTimeLeft / 60)}:${(paymentTimeLeft % 60).toString().padStart(2, '0')}`}
                                </Typography>
                            </Box>
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <PaymentForm 
                            ref={paymentFormRef}
                            amount={parseInt(paymentDetails?.amount, 10)}
                            codeValue={selectedCode?.code_value}
                            reason={paymentDetails?.reason}
                            codeFormData={codeForm}
                            onSuccess={handlePaymentSuccess}
                            onError={(error) => {
                                console.error('Payment failed:', error);
                            }}
                            onCancel={handlePaymentCancel}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => handlePaymentModalClose(null, 'buttonClick')}>
                            Cancel
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}
        </Dialog>
    );
};

CodeForm.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    editingCode: PropTypes.object,
    onCodeSaved: PropTypes.func.isRequired
};

export default CodeForm; 